import React, { useState, useEffect, useRef } from 'react'
import { Download, Trash, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { isMobile } from 'react-device-detect'
import classNames from 'classnames'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { GalleryItem } from '../types'
import { ReactComponent as TrashIcon } from '../../../../svg/trash-2.svg'
import { ReactComponent as VideoDownloadIcon } from '../../../../svg/video-download.svg'
import { VideoCreationConfirmModal } from './VideoCreationConfirmModal'
import { DeleteConfirmModal } from './DeleteConfirmModal'
import { downloadFile } from '../../../../utils/download'

interface ResponsiveSize {
  width: number
  height: number
  controlWidth: number
  navButtonWidth: number
  navButtonHeight: number
}

interface VideoDownloadModalProps {
  isOpen: boolean
  onClose: () => void
  item: GalleryItem | null
  allItems: GalleryItem[]
  currentIndex: number
  onNavigate: (index: number) => void
  onVideoGenerate?: (item: GalleryItem) => void
  onDelete?: (item: GalleryItem) => void
  // 페이지네이션 관련
  currentPage?: number
  totalPages?: number
  onNextPage?: () => void
  onPrevPage?: () => void
}

export const VideoDownloadModal: React.FC<VideoDownloadModalProps> = ({
  isOpen,
  onClose,
  item,
  allItems,
  currentIndex,
  onNavigate,
  onVideoGenerate,
  onDelete,
  currentPage,
  totalPages,
  onNextPage,
  onPrevPage,
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isVideoGenerating, setIsVideoGenerating] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [responsiveSize, setResponsiveSize] = useState<ResponsiveSize>({
    width: 320,
    height: 680,
    controlWidth: 320,
    navButtonWidth: 22,
    navButtonHeight: 86,
  })
  const [isPageChanging, setIsPageChanging] = useState(false)
  const [pageDirection, setPageDirection] = useState<'next' | 'prev' | null>(null)
  const [isChangingPage, setIsChangingPage] = useState(false)
  const sliderRef = useRef<Slider>(null)

  // 반응형 크기 계산 함수
  const calculateResponsiveSize = (): ResponsiveSize => {
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight

    // Figma 디자인 기준 비율 (682:1214)
    const designRatio = 682 / 1214

    // 최소/최대 크기 제한
    const minWidth = isMobile ? 280 : 320
    const maxWidth = 682
    const maxHeight = 1214

    // 화면 크기에 따른 최적 크기 계산
    const availableWidth = Math.min(screenWidth * 0.9, maxWidth)
    const availableHeight = Math.min(screenHeight * 0.85, maxHeight)

    // 비율 유지하면서 크기 조정
    let finalWidth = Math.max(availableWidth, minWidth)
    let finalHeight = finalWidth / designRatio

    if (finalHeight > availableHeight) {
      finalHeight = availableHeight
      finalWidth = finalHeight * designRatio
      finalWidth = Math.max(finalWidth, minWidth)
    }

    // 네비게이션 버튼 크기 계산 (콘텐츠 크기에 비례)
    const sizeRatio = finalWidth / 682 // 디자인 기준 대비 비율
    const navButtonWidth = Math.max(
      isMobile ? 20 : 22,
      Math.round(22 * sizeRatio)
    )
    const navButtonHeight = Math.max(
      isMobile ? 70 : 86,
      Math.round(86 * sizeRatio)
    )

    return {
      width: Math.round(finalWidth),
      height: Math.round(finalHeight),
      controlWidth: Math.round(finalWidth),
      navButtonWidth,
      navButtonHeight,
    }
  }

  // 윈도우 리사이즈 이벤트 핸들링
  useEffect(() => {
    const handleResize = () => {
      setResponsiveSize(calculateResponsiveSize())
    }

    // 초기 크기 설정
    handleResize()

    if (isOpen) {
      window.addEventListener('resize', handleResize)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen])

  // 키보드 네비게이션 (ESC, 화살표 키)
  useEffect(() => {
    const handleKeyNavigation = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          event.preventDefault()
          handlePrevImage()
          break
        case 'ArrowRight':
          event.preventDefault()
          handleNextImage()
          break
        default:
          break
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyNavigation)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyNavigation)
    }
  }, [isOpen, onClose, currentIndex, allItems.length])

  // allItems 변경 감지 (페이지 변경 완료 후 상태 초기화)
  useEffect(() => {
    console.log('📊 allItems useEffect:', { 
      isPageChanging, 
      pageDirection, 
      allItemsLength: allItems.length 
    })
    
    if (isPageChanging && allItems.length > 0 && pageDirection) {
      // 먼저 올바른 인덱스로 onNavigate 호출
      const targetIndex = pageDirection === 'next' ? 0 : allItems.length - 1
      console.log('🎯 Setting target index for page change:', { pageDirection, targetIndex })
      
      // 슬라이더 초기화 후 onNavigate 호출
      setTimeout(() => {
        onNavigate(targetIndex)
        console.log('✅ Called onNavigate with target index:', targetIndex)
      }, 100)
      
      // 상태 리셋은 더 늦게
      const resetTimer = setTimeout(() => {
        console.log('✅ Resetting page changing state (delayed)')
        setIsPageChanging(false)
        setPageDirection(null)
      }, 400) // 더 긴 지연으로 안정성 향상
      
      return () => clearTimeout(resetTimer)
    }
  }, [allItems, isPageChanging, pageDirection, onNavigate])

  // 데스크톱에서 이미지 변경 시 모바일 슬라이더 동기화
  useEffect(() => {
    if (isMobile && sliderRef.current && !isPageChanging && !pageDirection) {
      sliderRef.current.slickGoTo(currentIndex)
    }
  }, [currentIndex, isPageChanging, pageDirection])

  if (!isOpen || !item) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const totalImages = allItems.length

  // 페이지네이션 처리를 위한 onEdge 핸들러
  const handleSliderEdge = (direction: string) => {
    console.log('🚀 handleSliderEdge:', { direction, isChangingPage, currentPage, totalPages })
    
    if (isChangingPage) return // 이미 변경 중이면 무시
    
    if (direction === 'left') {
      // 마지막 이미지에서 왼쪽으로 스와이프 시도 - 다음 페이지
      if (totalPages && currentPage && onNextPage && currentPage < totalPages) {
        console.log('📄 Going to NEXT page')
        setIsChangingPage(true)
        setIsPageChanging(true)
        setPageDirection('next')
        onNextPage()
        
        // 500ms 후 디바운싱 해제
        setTimeout(() => {
          setIsChangingPage(false)
        }, 500)
      }
    } else if (direction === 'right') {
      // 첫 번째 이미지에서 오른쪽으로 스와이프 시도 - 이전 페이지
      if (totalPages && currentPage && onPrevPage && currentPage > 1) {
        console.log('📄 Going to PREV page')
        setIsChangingPage(true)
        setIsPageChanging(true)
        setPageDirection('prev')
        onPrevPage()
        
        // 500ms 후 디바운싱 해제
        setTimeout(() => {
          setIsChangingPage(false)
        }, 500)
      }
    }
  }

  // 모바일 슬라이더 설정
  const getInitialSlide = () => {
    // 빈 배열일 때 안전한 기본값 반환
    if (allItems.length === 0) {
      console.log('🔍 getInitialSlide - empty array, returning 0')
      return 0
    }
    
    let initialSlide = 0
    
    if (isPageChanging && pageDirection) {
      // 페이지 변경 중일 때만 특별한 로직 적용
      if (pageDirection === 'next') {
        // 다음 페이지로 이동: 첫 번째 이미지 표시
        initialSlide = 0
      } else if (pageDirection === 'prev') {
        // 이전 페이지로 이동: 마지막 이미지 표시
        initialSlide = allItems.length - 1
      }
    } else {
      // 일반적인 경우: currentIndex 사용하되 안전 범위 내로 제한
      initialSlide = Math.max(0, Math.min(currentIndex, allItems.length - 1))
    }
      
    console.log('🔍 getInitialSlide:', {
      isPageChanging,
      pageDirection,
      currentIndex,
      allItemsLength: allItems.length,
      calculatedSlide: initialSlide
    })
    
    return initialSlide
  }

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchMove: true,
    arrows: false,
    initialSlide: getInitialSlide(),
    afterChange: (current: number) => {
      console.log('🎯 afterChange:', { current, previousCurrentIndex: currentIndex, isPageChanging })
      // 페이지 변경 중이 아닐 때만 onNavigate 호출하여 인덱스 점프 방지
      if (!isPageChanging) {
        onNavigate(current)
      }
    },
    onEdge: handleSliderEdge,
    swipe: true,
    draggable: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          swipe: true,
          draggable: true,
        },
      },
    ],
  }

  const handlePrevImage = () => {
    if (currentIndex > 0) {
      // 첫 번째가 아니면 이전 이미지
      const newIndex = currentIndex - 1
      onNavigate(newIndex)
    } else if (totalPages && currentPage && onPrevPage && currentPage > 1) {
      // 첫 번째 이미지이고 이전 페이지가 있으면 페이지 이동
      setIsPageChanging(true)
      setPageDirection('prev')
      onPrevPage()
    }
  }

  const handleNextImage = () => {
    if (currentIndex < allItems.length - 1) {
      // 마지막이 아니면 다음 이미지
      const newIndex = currentIndex + 1
      onNavigate(newIndex)
    } else if (totalPages && currentPage && onNextPage && currentPage < totalPages) {
      // 마지막 이미지이고 다음 페이지가 있으면 페이지 이동
      setIsPageChanging(true)
      setPageDirection('next')
      onNextPage()
    }
  }



  const handleDelete = () => {
    setIsDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!item) return

    // 부모 컴포넌트에 삭제 요청 위임
    onDelete?.(item)
    
    // 삭제 후 다음 아이템으로 이동
    if (currentIndex < allItems.length - 1) {
      // 현재 인덱스 다음 아이템으로 이동
      onNavigate(currentIndex + 1)
    } else if (currentIndex > 0) {
      // 마지막 아이템이면 이전 아이템으로 이동
      onNavigate(currentIndex - 1)
    } else {
      // 아이템이 하나뿐이면 모달 닫기
      onClose()
    }
  }

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false)
  }

  const handleCreateClip = () => {
    setIsConfirmModalOpen(true)
  }

  const handleConfirmVideoCreation = async () => {
    if (!item || !onVideoGenerate) return

    setIsVideoGenerating(true)
    try {
      await onVideoGenerate(item)
      setIsConfirmModalOpen(false)
      onClose()
    } catch (error) {
      console.error('Video generation failed:', error)
    } finally {
      setIsVideoGenerating(false)
    }
  }

  const handleCancelVideoCreation = () => {
    setIsConfirmModalOpen(false)
  }

  const handleDownload = async () => {
    if (item?.status === 'completed' && item.downloadUrl) {
      const extension = item.type === 'style-clip' ? 'mp4' : 'jpg'
      const filename = `${item.type}-${item.id}.${extension}`
      const title = item.type === 'style-clip' ? '스타일클립 영상' : '스타일샷 이미지'
      const text = item.type === 'style-clip' ? 
        '스타일클립 영상을 저장하거나 공유하세요' : 
        '스타일샷 이미지를 저장하거나 공유하세요'

      await downloadFile(item.downloadUrl, filename, {
        title,
        text,
      })
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      {isMobile ? (
        /* 모바일 레이아웃: react-slick 슬라이더 */
        <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
          {/* 모바일 닫기 버튼 */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
          >
            <X size={24} className="text-white" />
          </button>

          {/* react-slick 슬라이더 */}
          <div className="relative" style={{ width: '315px', height: '561px' }}>
            {allItems.length > 0 ? (
              <Slider 
                key={`slider-${currentPage || 0}-${allItems.length}-${isPageChanging ? pageDirection : 'normal'}`}
                ref={sliderRef} 
                {...sliderSettings}
              >
                {allItems.map((slideItem, index) => (
                <div key={slideItem.id} className="outline-none">
                  {slideItem.type === 'style-shot' ? (
                    <img
                      src={slideItem.imageUrl}
                      alt={`Preview ${index + 1}`}
                      className="object-cover select-none"
                      style={{
                        width: '315px',
                        height: '561px',
                      }}
                      draggable={false}
                    />
                  ) : (
                    <video
                      src={slideItem.videoUrl}
                      controls={index === currentIndex} // 현재 슬라이드만 컨트롤 표시
                      autoPlay={index === currentIndex}
                      loop
                      className="object-cover select-none"
                      style={{
                        width: '315px',
                        height: '561px',
                      }}
                    />
                  )}
                </div>
                ))}
              </Slider>
            ) : (
              // 데이터가 없을 때 로딩 상태 표시
              <div className="flex items-center justify-center w-full h-full bg-gray-200 rounded-[10px]">
                <div className="text-gray-500 text-sm">로딩 중...</div>
              </div>
            )}
          </div>

          {/* 모바일 하단 컨트롤 - 화면 아래 고정 */}
          <div
            className="absolute bottom-0 bg-[#2E343D] p-4"
            style={{
              width: '375px',
              height: '83px',
            }}
          >
            {/* 페이지 인디케이터 - 이미지와 하단 컨트롤에 겹쳐서 플로팅 */}
            <div
              className={classNames(
                'absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#252525] rounded-[10px] px-4 py-1 w-[64px] h-[22px] flex items-center justify-center z-10 bottom-20',
                {}
              )}
            >
              <span className="text-white text-xs">
                {currentIndex + 1}/{totalImages}
              </span>
            </div>

            {/* 액션 버튼들 - Figma 디자인에 맞게 분리된 버튼들 */}
            <div className="flex gap-1">
              {/* 삭제 버튼 */}
              <button
                onClick={handleDelete}
                className="bg-[#424D5E] rounded-[10px] flex-1 h-[51px] flex items-center justify-center gap-1 hover:bg-[#4a5561] transition-colors"
              >
                <Trash width={16} height={16} stroke="#FFFFFF" />
                <span className="text-white text-sm">삭제</span>
              </button>

              {/* 클립 생성 버튼 - style-shot일 때만 표시 */}
              {item.type === 'style-shot' && (
                <button
                  onClick={handleCreateClip}
                  className="bg-[#424D5E] rounded-[10px] flex-1 h-[51px]  flex items-center justify-center gap-1 hover:bg-[#4a5561] transition-colors"
                >
                  <VideoDownloadIcon width={16} height={16} />
                  <span className="text-white text-sm">클립 생성</span>
                </button>
              )}

              {/* 다운로드 버튼 */}
              <button
                onClick={handleDownload}
                className="bg-[#424D5E] text-white rounded-[10px] flex-1 h-[51px]  flex items-center justify-center gap-1 hover:bg-[#4a5561] transition-colors"
              >
                <Download size={16} />
                <span className="text-white text-sm">다운로드</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* 데스크톱 레이아웃: 기존 flex 구조 */
        <div className="relative flex items-center justify-center gap-8">
          {/* 왼쪽 네비게이션 버튼 */}
          <button
            onClick={handlePrevImage}
            className="bg-[#373B42] border border-[rgba(255,255,255,0.2)] rounded-md px-1 py-4 hover:bg-[rgba(55,59,66,0.9)] transition-colors flex items-center justify-center"
            style={{
              width: `${responsiveSize.navButtonWidth}px`,
              height: `${responsiveSize.navButtonHeight}px`,
            }}
          >
            <ChevronLeft size={16} className="text-white" />
          </button>

          {/* 중앙 콘텐츠 */}
          <div className="relative flex flex-col items-center gap-2">
            {/* 메인 콘텐츠 (이미지/비디오) */}
            <div className="relative">
              {item.type === 'style-shot' ? (
                <img
                  src={item.imageUrl}
                  alt="Main preview"
                  className="object-cover rounded-[10px]"
                  style={{
                    width: `${responsiveSize.width}px`,
                    maxHeight: `${responsiveSize.height}px`,
                  }}
                />
              ) : (
                <video
                  src={item.videoUrl}
                  controls
                  autoPlay
                  loop
                  className="object-cover rounded-[10px]"
                  style={{
                    width: `${responsiveSize.width}px`,
                    maxHeight: `${responsiveSize.height}px`,
                  }}
                />
              )}
            </div>

            {/* 데스크톱 하단 컨트롤 */}
            <div
              className="bg-[#2E343D] rounded-[10px] p-4"
              style={{
                width: `${responsiveSize.controlWidth}px`,
              }}
            >
              {/* 페이지 인디케이터 - 이미지와 하단 컨트롤에 겹쳐서 플로팅 */}
              <div
                className={classNames(
                  'absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#252525] rounded-[10px] px-4 py-1 w-[64px] h-[22px] flex items-center justify-center z-10 bottom-11',
                  {}
                )}
              >
                <span className="text-white text-xs">
                  {currentIndex + 1}/{totalImages}
                </span>
              </div>

              {/* 액션 버튼들 */}
              <div className="flex justify-center">
                <div className="bg-[#424D5E] rounded-[4px] px-4 py-1 flex items-center gap-4">
                  {/* 삭제 버튼 */}
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-1 text-white text-xs hover:text-gray-300 transition-colors h-[22px]"
                  >
                    <Trash width={16} height={16} stroke="#FFFFFF" />
                    <span>삭제</span>
                  </button>

                  {/* 클립 생성 버튼 - style-shot일 때만 표시 */}
                  {item.type === 'style-shot' && (
                    <button
                      onClick={handleCreateClip}
                      className="flex items-center gap-1 text-white text-xs hover:text-gray-300 transition-colors h-[22px]"
                    >
                      <VideoDownloadIcon width={16} height={16} />
                      <span>클립 생성</span>
                    </button>
                  )}

                  {/* 다운로드 버튼 */}
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1 text-white text-xs hover:text-gray-300 transition-colors h-[22px]"
                  >
                    <Download size={16} />
                    <span>다운로드</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 오른쪽 네비게이션 버튼 */}
          <button
            onClick={handleNextImage}
            className="bg-[#373B42] border border-[rgba(255,255,255,0.2)] rounded-md px-1 py-4 hover:bg-[rgba(55,59,66,0.9)] transition-colors flex items-center justify-center"
            style={{
              width: `${responsiveSize.navButtonWidth}px`,
              height: `${responsiveSize.navButtonHeight}px`,
            }}
          >
            <ChevronRight size={16} className="text-white" />
          </button>
        </div>
      )}

      {/* 비디오 생성 확인 모달 */}
      <VideoCreationConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={handleCancelVideoCreation}
        onConfirm={handleConfirmVideoCreation}
        item={item}
        isLoading={isVideoGenerating}
      />

      {/* 삭제 확인 모달 */}
      <DeleteConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        item={item}
      />
    </div>
  )
}
